<template>
  <div class="landing-wrapper" style="background-color: #050505; min-height: 100vh; font-family: 'Inter', 'Outfit', sans-serif; color: #e5e7eb;">
    
    <!-- State 1: Loading State -->
    <div v-if="loading" class="d-flex justify-content-center align-items-center" style="height: 100vh; background-color: #050505;">
      <div class="spinner-border" style="width: 4rem; height: 4rem; color: #18d600;" role="status"></div>
    </div>

    <!-- State 2: Error State -->
    <div v-else-if="error" class="container py-5 text-center">
      <div class="card border-0 shadow-lg p-5 text-center my-5 mx-auto" style="border-radius: 20px; max-width: 600px; background-color: #111; border: 1px solid #333 !important;">
        <div class="mb-4">
          <i class="fas fa-exclamation-circle text-danger" style="font-size: 72px;"></i>
        </div>
        <h3 class="font-weight-bold mb-3 text-white">Curso no encontrado</h3>
        <p class="text-muted mb-4">El curso que estás buscando no existe o ya no está disponible.</p>
      </div>
    </div>

    <!-- State 3: Landing View -->
    <div v-else class="pb-5 pt-4">
      
      <!-- Top Logo (Optional) -->
      <div class="container mb-4">
        <img src="@/assets/logos/navbar-w.webp" alt="Promolíder" style="height: 30px; object-fit: contain;" />
      </div>

      <div class="container" style="max-width: 1100px;">
        <div class="row">
          
          <!-- LEFT COLUMN: CONTENT -->
          <div class="col-12 col-lg-8 mb-5 pr-lg-4">
            
            <!-- Cover Image Inline -->
            <div style="width: 100%; border-radius: 16px; overflow: hidden; margin-bottom: 24px; border: 1px solid #222;">
              <img :src="getMediaUrl(courseInfo.photo || courseInfo.url_portada)" style="width: 100%; height: auto; max-height: 450px; object-fit: cover;" alt="Cover" />
            </div>
            
            <div class="d-flex align-items-center gap-2 mb-3">
              <span style="background-color: rgba(24, 214, 0, 0.15); color: #18d600; font-size: 10px; font-weight: 700; padding: 4px 10px; border-radius: 4px; text-transform: uppercase;">
                {{ courseInfo.category?.name || 'DESARROLLO WEB' }}
              </span>
            </div>

            <h1 style="font-size: 32px; font-weight: 800; color: #fff; margin-bottom: 20px; line-height: 1.2;">
              {{ courseInfo.title }}
            </h1>
            
            <div class="d-flex align-items-center gap-2 mb-4">
              <span style="font-size: 13px; color: #9ca3af;">Creado por</span>
              <div class="d-flex align-items-center gap-2">
                <img v-if="instructorPhoto" :src="instructorPhoto" style="width: 24px; height: 24px; border-radius: 50%; object-fit: cover;" />
                <div v-else style="width: 24px; height: 24px; border-radius: 50%; background-color: #ea580c; color: white; display: flex; align-items: center; justify-content: center; font-weight: bold; font-size: 11px;">
                  {{ instructorInitials }}
                </div>
                <span style="color: #fff; font-weight: 600; font-size: 13px;">{{ instructorName }}</span>
              </div>
            </div>

            <p style="font-size: 16px; color: #d1d5db; margin-bottom: 30px; line-height: 1.6; font-weight: 300;">
              {{ courseInfo.description || 'Descubre todo lo que este curso tiene preparado para ti. ¡Inscríbete hoy mismo!' }}
            </p>

            <!-- What you will learn -->
            <div v-if="whatWillLearnList.length > 0" style="background-color: #0f0f0f; border: 1px solid #222; border-radius: 16px; padding: 30px; margin-bottom: 30px;">
              <h2 style="font-size: 20px; font-weight: 700; color: #fff; margin-bottom: 20px;">Lo que lograrás</h2>
              <ul style="list-style: none; padding: 0; margin: 0;">
                <li v-for="(item, index) in whatWillLearnList" :key="index" class="d-flex align-items-start mb-3">
                  <div style="margin-top: 4px; margin-right: 12px; color: #3b82f6; flex-shrink: 0;">
                    <i class="fas fa-check-square"></i>
                  </div>
                  <span style="color: #d1d5db; font-size: 14px; line-height: 1.5;">{{ item }}</span>
                </li>
              </ul>
              <div style="margin-top: 20px; font-size: 13px; color: #d1d5db;">
                <div class="mb-1"><span style="color: #eab308; margin-right: 8px;">👉</span> 100% práctico, paso a paso y sin rodeos teóricos.</div>
                <div><span style="color: #9ca3af; margin-right: 8px;">🔗</span> ¡Inscríbete hoy y acelera tu perfil tech!</div>
              </div>
            </div>

            <!-- Features Grid -->
            <div class="row g-3 mb-5">
              <div class="col-12 col-md-6">
                <div class="d-flex align-items-center gap-3 p-3" style="border: 1px solid #222; border-radius: 12px; background-color: #0a0a0a;">
                  <div style="width: 40px; height: 40px; background-color: #111; border-radius: 8px; display: flex; align-items: center; justify-content: center; color: #9ca3af;">
                    <i class="far fa-calendar-check"></i>
                  </div>
                  <div>
                    <h4 style="color: #fff; font-size: 13px; font-weight: 700; margin: 0;">Disponibilidad</h4>
                    <p style="color: #9ca3af; font-size: 11px; margin: 0;">Acceso Inmediato</p>
                  </div>
                </div>
              </div>
              <div class="col-12 col-md-6">
                <div class="d-flex align-items-center gap-3 p-3" style="border: 1px solid #222; border-radius: 12px; background-color: #0a0a0a;">
                  <div style="width: 40px; height: 40px; background-color: #111; border-radius: 8px; display: flex; align-items: center; justify-content: center; color: #9ca3af;">
                    <i class="fas fa-map-marker-alt"></i>
                  </div>
                  <div>
                    <h4 style="color: #fff; font-size: 13px; font-weight: 700; margin: 0;">Ubicación virtual</h4>
                    <p style="color: #9ca3af; font-size: 11px; margin: 0;">Disponible al completar tu registro</p>
                  </div>
                </div>
              </div>
            </div>

            <!-- Creator -->
            <div class="mb-5">
              <h2 style="font-size: 20px; font-weight: 700; color: #fff; margin-bottom: 24px;">Acerca del creador</h2>
              <div class="d-flex align-items-start gap-4">
                <div style="flex-shrink: 0;">
                  <img v-if="instructorPhoto" :src="instructorPhoto" style="width: 80px; height: 80px; border-radius: 50%; object-fit: cover;" />
                  <div v-else style="width: 80px; height: 80px; border-radius: 50%; background-color: #ea580c; color: white; display: flex; align-items: center; justify-content: center; font-weight: bold; font-size: 28px;">
                    {{ instructorInitials }}
                  </div>
                </div>
                <div>
                  <h3 style="font-size: 18px; font-weight: 700; color: #fff; margin-bottom: 8px;">{{ instructorName }}</h3>
                  <div class="d-flex gap-2 mb-3">
                    <span style="background-color: #111; border: 1px solid #333; color: #d1d5db; font-size: 9px; font-weight: 700; padding: 2px 8px; border-radius: 4px; text-transform: uppercase;">Productor</span>
                    <span style="background-color: #111; border: 1px solid #333; color: #d1d5db; font-size: 9px; font-weight: 700; padding: 2px 8px; border-radius: 4px; text-transform: uppercase;">Experto</span>
                  </div>
                  <p style="font-size: 13px; color: #9ca3af; line-height: 1.6; margin: 0;">{{ instructorBio }}</p>
                </div>
              </div>
            </div>

          </div>

          <!-- RIGHT COLUMN: STICKY CARD -->
          <div class="col-12 col-lg-4">
            <div class="position-sticky" style="top: 2rem; background-color: #111; border: 1px solid #222; border-radius: 20px; padding: 24px; box-shadow: 0 20px 25px -5px rgba(0,0,0,0.5);">
              <h3 style="color: #9ca3af; font-size: 11px; font-weight: 400; text-transform: uppercase; margin-bottom: 4px;">Plan completo</h3>
              <div style="font-size: 32px; font-weight: 800; color: #fff; margin-bottom: 16px;">
                {{ formatPrice(courseInfo.price || courseInfo.price_base) }}
              </div>
              
              <div style="display: flex; align-items: center; gap: 8px; margin-bottom: 20px; background-color: rgba(234, 179, 8, 0.1); border: 1px solid rgba(234, 179, 8, 0.2); border-radius: 8px; padding: 8px 12px;">
                <i class="far fa-clock" style="color: #eab308; font-size: 12px;"></i>
                <span style="color: #eab308; font-size: 11px; font-weight: 600;">Las inscripciones están por cerrar</span>
              </div>

              <div class="d-flex gap-2 mb-4">
                <button style="background-color: #222; border: 1px solid #333; color: white; width: 44px; height: 44px; border-radius: 8px; flex-shrink: 0; display: flex; align-items: center; justify-content: center;">
                  <i class="far fa-bookmark"></i>
                </button>
                <button style="background-color: #222; border: 1px solid #333; color: white; width: 44px; height: 44px; border-radius: 8px; flex-shrink: 0; display: flex; align-items: center; justify-content: center;">
                  <i class="fas fa-share-alt"></i>
                </button>
                <button @click="openRegisterModal" style="background-color: #fff; color: #000; border: none; font-weight: 700; font-size: 14px; height: 44px; border-radius: 8px; flex-grow: 1; transition: all 0.2s;">
                  Obtener acceso
                </button>
              </div>

              <div style="text-align: center; font-size: 11px; color: #6b7280; display: flex; align-items: center; justify-content: center; gap: 6px;">
                <i class="fas fa-lock"></i>
                <span>Pago 100% seguro y encriptado</span>
              </div>
            </div>
          </div>

        </div>
      </div>
      
    </div>

    <!-- MODAL DE REGISTRO & UPSELL (MEMBRESIAS) -->
    <div v-if="showRegisterModal" class="tw-fixed tw-inset-0 tw-z-50 tw-flex tw-items-center tw-justify-center tw-px-4" style="position: fixed; top:0; left:0; right:0; bottom:0; background: rgba(0,0,0,0.85); z-index: 9999; display: flex; align-items: center; justify-content: center; padding: 1rem;">
      <div style="background-color: #111; border: 1px solid #333; border-radius: 24px; box-shadow: 0 25px 50px -12px rgba(0,0,0,0.5); width: 100%; max-width: 900px; position: relative; display: flex; flex-wrap: wrap; overflow: hidden;">
        
        <button @click="closeRegisterModal" style="position: absolute; top: 16px; right: 16px; background: #222; border: none; color: #9ca3af; width: 32px; height: 32px; border-radius: 50%; display: flex; align-items: center; justify-content: center; cursor: pointer; z-index: 20;">
          <i class="fas fa-times"></i>
        </button>

        <!-- OPCION 1: ESTANDAR -->
        <div class="col-12 col-md-6" style="padding: 40px; border-right: 1px solid #222;">
          <div style="margin-bottom: 24px;">
            <span style="background-color: #222; color: #d1d5db; font-size: 10px; font-weight: 700; padding: 4px 12px; border-radius: 12px; text-transform: uppercase;">Cuenta Estándar</span>
          </div>
          <h3 style="font-size: 24px; font-weight: 700; color: #fff; margin-bottom: 8px;">Solo quiero este curso</h3>
          <p style="color: #9ca3af; font-size: 14px; margin-bottom: 24px;">Crea una cuenta gratuita en Promolíder y obtén acceso de por vida únicamente a este curso por el precio normal.</p>
          
          <div style="font-size: 30px; font-weight: 800; color: #fff; margin-bottom: 32px;">
            {{ formatPrice(courseInfo.price || courseInfo.price_base) }}
          </div>
          
          <button @click="goToRegister('free')" class="btn-free-pulse" style="width: 100%; background-color: #222; color: white; font-weight: 700; padding: 12px; border-radius: 12px; margin-top: auto; transition: all 0.3s ease;">
            Continuar con Cuenta Gratis
          </button>
        </div>

        <!-- OPCION 2: MEMBRESIA VIP -->
        <div class="col-12 col-md-6" style="padding: 40px; background: linear-gradient(135deg, #0a0a0a 0%, #111 100%);">
          <div style="margin-bottom: 24px;">
            <span style="background-color: rgba(24, 214, 0, 0.15); color: #18d600; font-size: 10px; font-weight: 700; padding: 4px 12px; border-radius: 12px; text-transform: uppercase;"><i class="fas fa-crown mr-1"></i> Recomendado</span>
          </div>
          <h3 style="font-size: 24px; font-weight: 700; color: #fff; margin-bottom: 8px;">Únete a Promolíder VIP</h3>
          <p style="color: #9ca3af; font-size: 14px; margin-bottom: 20px;">Al adquirir una Membresía VIP obtienes <strong>descuentos exclusivos</strong> en este y todos los cursos, además de generar comisiones por tu red.</p>
          
          <div v-if="memberships.length > 0" style="margin-bottom: 16px;">
            <label style="color: #9ca3af; font-size: 12px; margin-bottom: 6px; display: block; font-weight: 600;">Selecciona un plan:</label>
            <select v-model="featuredMembershipIndex" @change="updateFeaturedMembership" style="width: 100%; background-color: #000; border: 1px solid #333; color: white; padding: 10px 16px; border-radius: 10px; outline: none; font-weight: 600; font-size: 14px; appearance: none; cursor: pointer; background-image: url('data:image/svg+xml;charset=US-ASCII,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%22292.4%22%20height%3D%22292.4%22%3E%3Cpath%20fill%3D%22%239ca3af%22%20d%3D%22M287%2069.4a17.6%2017.6%200%200%200-13-5.4H18.4c-5%200-9.3%201.8-12.9%205.4A17.6%2017.6%200%200%200%200%2082.2c0%205%201.8%209.3%205.4%2012.9l128%20127.9c3.6%203.6%207.8%205.4%2012.8%205.4s9.2-1.8%2012.8-5.4L287%2095c3.5-3.5%205.4-7.8%205.4-12.8%200-5-1.9-9.2-5.5-12.8z%22%2F%3E%3C%2Fsvg%3E'); background-repeat: no-repeat; background-position: right 1rem top 50%; background-size: 0.65rem auto;">
              <option v-for="(m, i) in memberships" :key="i" :value="i">
                {{ m.account || m.name }} - ${{ m.price }} USD/AÑO
              </option>
            </select>
          </div>

          <div style="margin-bottom: 10px; width: 100%; display: flex; justify-content: center; overflow: hidden; margin-top: 0px;">
             <div style="width: 480px; transform: scale(0.75); transform-origin: center center; display: flex; justify-content: center;">
               <!-- Inyectamos el componente VIP -->
              <VipMembershipCard :membership="featuredMembership" :user="{ name: 'Nuevo', last_name: 'Usuario' }" />
             </div>
          </div>

          <button @click="goToRegister('vip')" style="width: 100%; background: linear-gradient(90deg, #18d600, #12a100); border: none; color: white; font-weight: 700; padding: 12px; border-radius: 12px; box-shadow: 0 0 15px rgba(24,214,0,0.3); margin-top: auto;">
            Registrarme como VIP
          </button>
        </div>
      </div>

      <!-- Modal de Registro Rápido -->
      <QuickRegisterModal 
        :show="showQuickRegister" 
        :type="registerType" 
        :ref-id="sponsorRefId" 
        :plan-id="selectedPlanId" 
        @close="showQuickRegister = false" 
        @success="handleRegisterSuccess" 
      />
    </div>
    
  </div>
</template>

<script>
import VipMembershipCard from "@/components/VipMembershipCard.vue";
import QuickRegisterModal from "@/components/QuickRegisterModal.vue";
import { authGet } from "@/helpers/authStorage";

export default {
  name: 'CourseLanding',
  components: {
    VipMembershipCard,
    QuickRegisterModal
  },
  data() {
    return {
      loading: true,
      error: false,
      courseInfo: {},
      showRegisterModal: false, // old modal, keeping for compatibility if used elsewhere
      showQuickRegister: false,
      registerType: 'free',
      sponsorRefId: '',
      selectedPlanId: '',
      memberships: [],
      featuredMembership: null,
      featuredMembershipIndex: 0
    }
  },
  computed: {
    instructorName() {
      const u = this.courseInfo.instructor || this.courseInfo.user;
      if (!u) return 'Promolíder';
      return `${u.name || ''} ${u.last_name || ''}`.trim() || 'Promolíder';
    },
    instructorInitials() {
      const name = this.instructorName;
      if (name === 'Promolíder') return 'P';
      return name.charAt(0).toUpperCase();
    },
    instructorPhoto() {
      const u = this.courseInfo.instructor || this.courseInfo.user;
      if (u && u.photo && !u.photo.includes('avatar1.png')) {
        return this.getMediaUrl(u.photo);
      }
      return null;
    },
    instructorBio() {
      const u = this.courseInfo.instructor || this.courseInfo.user;
      return u?.biography || 'Productor de contenido experto y aliado oficial de Promolíder.';
    },
    whatWillLearnList() {
      if (this.courseInfo.what_will_learn) {
        return this.parseList(this.courseInfo.what_will_learn);
      }
      // Fallback a partir de description si no hay what_will_learn
      if (this.courseInfo.description) {
        const sentences = this.courseInfo.description.split('. ').filter(s => s.length > 10).slice(0, 4);
        if (sentences.length > 0) return sentences.map(s => s + (s.endsWith('.') ? '' : '.'));
      }
      return [
        "Dominar los conceptos clave desde cero hasta el nivel profesional.",
        "Aplicar los conocimientos en casos y proyectos 100% reales.",
        "Acelerar tu perfil profesional y destacar en el mercado."
      ];
    }
  },
  mounted() {
    this.captureReferral();
    this.fetchCourse();
    this.fetchMemberships();
  },
  methods: {
    getMediaUrl(path) {
      if (!path) return '';
      if (path.includes('api.promolider.email')) {
        path = path.replace(/https?:\/\/api\.promolider\.email/g, '');
      }
      if (path.startsWith('http://') || path.startsWith('https://')) return path;
      const cleanPath = path.startsWith('/') ? path : '/' + path;
      return 'https://promolider-storage-user.s3-accelerate.amazonaws.com' + cleanPath;
    },
    formatPrice(price) {
      if (!price || parseFloat(price) === 0) return 'Gratis';
      return `$${parseFloat(price).toFixed(2)} USD`;
    },
    parseList(text) {
      if (!text) return [];
      if (typeof text === 'string') {
        return text.split(/\r?\n|;/).map(s => s.trim()).filter(Boolean);
      }
      return Array.isArray(text) ? text : [];
    },
    captureReferral() {
      const refId = this.$route.query.ref;
      if (refId) {
        localStorage.setItem('sponsor_ref_id', refId);
      }
    },
    async fetchCourse() {
      try {
        const courseId = this.$route.params.id; 
        const response = await this.axios.get("course/details/" + courseId);
        this.courseInfo = (response && response.data && response.data.data) ? response.data.data : {};
        
        if (!this.courseInfo.id) {
          this.error = true;
        }
      } catch (err) {
        console.error("Error cargando curso para landing: ", err);
        this.error = true;
      } finally {
        this.loading = false;
      }
    },
    async fetchMemberships() {
      try {
        // Obtenemos los planes de membresía públicos
        const response = await this.axios.get("/public/membership-plans");
        
        let plans = [];
        if (response.data && Array.isArray(response.data.data)) {
          plans = response.data.data;
        } else if (response.data && Array.isArray(response.data)) {
          plans = response.data;
        }

        if (plans.length > 0) {
          this.memberships = plans;
          let maxIndex = 0;
          let maxPrice = -1;
          this.memberships.forEach((m, i) => {
            let p = parseFloat(m.price) || 0;
            if (p > maxPrice) { maxPrice = p; maxIndex = i; }
          });
          this.featuredMembershipIndex = maxIndex;
          this.featuredMembership = this.memberships[maxIndex];
        } else {
          throw new Error("No memberships found in API response");
        }
      } catch (err) {
        console.error("Error cargando membresias para landing: ", err);
        // Si falla, creamos membresias de prueba
        this.memberships = [
          { account: 'Academy', price: '305.00', iva: 18, disc_purchases_course: 15, pay_in_binary: 30, fast_cash_bonus: 18, comission: 20, course_selling_bonus: 20, productor_bonus: 20 },
          { account: 'University', price: '805.00', iva: 18, disc_purchases_course: 20, pay_in_binary: 40, fast_cash_bonus: 20, comission: 30, course_selling_bonus: 25, productor_bonus: 30 },
          { account: 'Socio Fundador', price: '1313.56', iva: 18, disc_purchases_course: 25, pay_in_binary: 50, fast_cash_bonus: 20, comission: 0, course_selling_bonus: 25, productor_bonus: 30 }
        ];
        this.featuredMembershipIndex = 2;
        this.featuredMembership = this.memberships[2]; // Socio Fundador
      }
    },
    updateFeaturedMembership() {
      this.featuredMembership = this.memberships[this.featuredMembershipIndex];
    },
    openRegisterModal() {
      if (authGet('access_token')) {
        const slug = this.courseInfo?.slug || 'curso';
        const cId = this.courseInfo?.id || this.$route.params.id || this.courseId;
        window.location.href = `/buy-cursos/${cId}/${slug}`;
      } else {
        this.showRegisterModal = true;
      }
    },
    closeRegisterModal() {
      this.showRegisterModal = false;
    },
    goToRegister(type) {
      this.sponsorRefId = this.$route.query.ref || localStorage.getItem('sponsor_ref_id') || '';
      this.registerType = type;
      
      if (type === 'vip' && this.featuredMembership) {
        this.selectedPlanId = this.featuredMembership.id || this.featuredMembership.account;
      } else {
        this.selectedPlanId = '';
      }

      this.showQuickRegister = true;
    },
    handleRegisterSuccess() {
      this.showQuickRegister = false;
      const slug = this.courseInfo?.slug || 'curso';
      const cId = this.courseInfo?.id || this.$route.params.id || this.courseId;
      // Redirigir al VCR marketplace (checkout) recargando la página para que Vuex tome la sesión
      window.location.href = `/buy-cursos/${cId}/${slug}`;
    }
  }
}
</script>

<style scoped>
/* Eliminar barras de scroll indeseadas para el modal */
:global(body.modal-open) {
  overflow: hidden;
}

@keyframes pulse-green-glow {
  0% {
    box-shadow: 0 0 0 0 rgba(24, 214, 0, 0.4);
    border: 1px solid rgba(24, 214, 0, 0.5);
  }
  70% {
    box-shadow: 0 0 0 10px rgba(24, 214, 0, 0);
    border: 1px solid rgba(24, 214, 0, 1);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(24, 214, 0, 0);
    border: 1px solid rgba(24, 214, 0, 0.5);
  }
}

.btn-free-pulse {
  animation: pulse-green-glow 2s infinite;
}
.btn-free-pulse:hover {
  background-color: #2a2a2a !important;
}
</style>
